import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { Send } from "lucide-react";


function fmtErr(d) {
    if (!d) return "";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
    return d?.msg || String(d);
}


/**
 * Admin-only "Compose WhatsApp" dialog.
 *
 * Fetches the ready-to-send template for the given member from the backend,
 * lets the admin tweak the copy, then triggers a server-side send via Gupshup
 * (with Twilio SMS as fallback). No wa.me / new tab.
 *
 * Props:
 *   open, onOpenChange – standard shadcn Dialog control
 *   member             – { name, phone, ...ids }
 *   variant            – "scoped"  (default): uses /scoped/year-roles/{year_role_id}/whatsapp-*
 *                        "legacy": uses /admin/members/{token}/whatsapp-*
 *   style              – "progress" (default) or "verify" (scoped only)
 */
export function WhatsAppComposeDialog({ open, onOpenChange, member, variant = "scoped", style = "progress" }) {
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [preview, setPreview] = useState(null);
    const [message, setMessage] = useState("");

    const routeId = variant === "legacy" ? member?.token : member?.year_role_id;
    const previewUrl = variant === "legacy"
        ? `/admin/members/${routeId}/whatsapp-preview`
        : `/scoped/year-roles/${routeId}/whatsapp-preview`;
    const sendUrl = variant === "legacy"
        ? `/admin/members/${routeId}/whatsapp-send`
        : `/scoped/year-roles/${routeId}/whatsapp-send`;

    useEffect(() => {
        if (!open || !routeId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setPreview(null);
            try {
                const params = variant === "scoped" ? { style } : undefined;
                const { data } = await api.get(previewUrl, params ? { params } : undefined);
                if (cancelled) return;
                setPreview(data);
                setMessage(data.message || "");
            } catch (e) {
                if (!cancelled) toast.error(fmtErr(e?.response?.data?.detail) || "Could not load preview");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, routeId, style, previewUrl, variant]);

    const send = async () => {
        if (!routeId) return;
        const body = (message || "").trim();
        if (!body) { toast.error("Message can't be empty"); return; }
        setSending(true);
        try {
            const { data } = await api.post(sendUrl, { message: body });
            if (data.ok) {
                const via = data.channel === "sms" ? "SMS (WhatsApp fallback)" : "WhatsApp";
                toast.success(`${via} sent to ${member.name}`);
                onOpenChange(false);
            } else {
                toast.error(`Send failed: ${data.detail || "unknown error"}`);
            }
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || "Send failed");
        } finally {
            setSending(false);
        }
    };

    // wa.me fallback — opens the recipient's WhatsApp with our message
    // pre-filled. Admin taps Send inside WhatsApp themselves. Reliable for
    // first-time outreach where the Gupshup session hasn't been established.
    const waMeUrl = () => {
        const raw = String(preview?.phone || member?.phone || "").replace(/\D+/g, "");
        // Data is stored as 10-digit national — prepend 91 for wa.me at link time.
        const digits = raw.length === 10 ? `91${raw}` : raw;
        const text = encodeURIComponent(message || "");
        return `https://wa.me/${digits}?text=${text}`;
    };
    const openWaMe = () => {
        const url = waMeUrl();
        if (!url.includes("wa.me/")) { toast.error("Recipient phone missing"); return; }
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg" data-testid="whatsapp-compose-dialog">
                <DialogHeader>
                    <DialogTitle className="rotary-heading text-2xl flex items-center gap-2">
                        <WhatsAppIcon className="h-5 w-5 text-[#25D366]" />
                        Send WhatsApp
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
                        <p><span className="text-muted-foreground">To:</span>{" "}
                           <span className="font-medium">{member?.name}</span>
                           <span className="text-muted-foreground"> · {preview?.phone || member?.phone}</span>
                        </p>
                    </div>

                    <div>
                        <Label className="label-eyebrow">Message</Label>
                        <Textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={7}
                            className="mt-1 font-mono text-[13px]"
                            data-testid="whatsapp-message-input"
                            placeholder={loading ? "Loading template…" : ""}
                            disabled={loading || sending}
                        />
                        <p className="mt-1 text-[11px] text-muted-foreground">
                            The link at the end opens the member&apos;s form. You can edit anything before hitting send.
                        </p>
                    </div>

                    {preview && !preview.gupshup_enabled && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                            WhatsApp provider isn&apos;t configured on the server. This will fall back to SMS if Twilio is set up, otherwise it&apos;ll fail.
                        </div>
                    )}

                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                        <p className="font-medium mb-1">Message not reaching?</p>
                        <p className="leading-relaxed">
                            WhatsApp Business drops first-time messages unless a pre-approved template is used. If the member hasn&apos;t
                            replied to us in the last 24 hours, tap <span className="font-medium">Open in WhatsApp</span> below to
                            send from your own account — reliable, but manual.
                        </p>
                    </div>
                </div>

                <DialogFooter className="gap-2 sm:justify-between">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={openWaMe}
                        disabled={loading || !message.trim() || !(preview?.phone || member?.phone)}
                        className="border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
                        data-testid="whatsapp-open-wame-btn"
                    >
                        <WhatsAppIcon className="mr-1.5 h-4 w-4" />
                        Open in WhatsApp
                    </Button>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={sending}
                            data-testid="whatsapp-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={send}
                            disabled={loading || sending || !message.trim()}
                            className="bg-[#25D366] text-white hover:bg-[#1ebe57]"
                            data-testid="whatsapp-send-btn"
                        >
                            <Send className="mr-1.5 h-4 w-4" />
                            {sending ? "Sending…" : "Send via server"}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
